import React, { useState } from 'react';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CRow,
  CCol,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CSpinner,
  CBadge
} from '@coreui/react';
import { apiService } from '../services/api';

const SkillPublisher = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [skillId, setSkillId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleAuthenticate = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await apiService.authenticateWithAmazon();
      setAccessToken(response.accessToken);
      setVendorId(response.vendorId);
      setSuccess('Authentication successful! Access token obtained.');
      setActiveStep(2);
    } catch (err) {
      setError(`Authentication failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSkill = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await apiService.createSkill(accessToken, vendorId);
      setSkillId(response.skillId);
      setSuccess(`Skill created successfully with ID: ${response.skillId}`);
      setActiveStep(3);
    } catch (err) {
      setError(`Skill creation failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuildModel = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Check if there's a stored invocation name for this skill
      const storedInvocationName = localStorage.getItem(`skill_${skillId}_invocationName`);
      console.log(`🔍 Building interaction model for skill ${skillId}`);
      console.log(`📦 Stored invocation name from localStorage:`, storedInvocationName || '(none)');
      const response = await apiService.buildInteractionModel(accessToken, skillId, storedInvocationName || undefined);
      console.log(`✅ Build interaction model response:`, response);
      setSuccess('Interaction model update initiated! ' + (response.message || 'Processing...'));
      
      // Set a timer to automatically proceed after a delay since the process happens asynchronously
      setTimeout(() => {
        if (!error) { // Only proceed if no errors occurred
          setActiveStep(4);
        }
      }, 3000);
    } catch (err) {
      setError(`Building interaction model failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnableTesting = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await apiService.enableTesting(accessToken, skillId);
      setSuccess('Testing enabled successfully!');
      setActiveStep(5);
    } catch (err) {
      setError(`Enabling testing failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitForCertification = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await apiService.submitForCertification(accessToken, skillId);
      setSuccess('Skill submitted for certification successfully!');
      setStatus('Submitted for certification');
      setActiveStep(6);
    } catch (err) {
      setError(`Submission for certification failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStatusIndicator = () => {
    if (isLoading) {
      return <CSpinner color="primary" />;
    }
    if (error) {
      return <div className="text-danger">{error}</div>;
    }
    if (success) {
      return <div className="text-success">{success}</div>;
    }
    return null;
  };

  return (
    <CCard>
      <CCardHeader>
        <h4>Publish Alexa Skill</h4>
      </CCardHeader>
      <CCardBody>
        {/* Step Progress Bar */}
        <CNav variant="tabs">
          {[1, 2, 3, 4, 5, 6].map(step => (
            <CNavItem key={step}>
              <CNavLink 
                active={activeStep === step}
                onClick={() => setActiveStep(step)}
                disabled={step > Math.max(activeStep, 1) || isLoading}
              >
                {step}. {step === 1 ? 'Authenticate' : 
                  step === 2 ? 'Create Skill' : 
                  step === 3 ? 'Build Model' : 
                  step === 4 ? 'Enable Testing' : 
                  step === 5 ? 'Submit' : 'Complete'}
                {activeStep > step && <CBadge color="success" className="ml-2">✓</CBadge>}
              </CNavLink>
            </CNavItem>
          ))}
        </CNav>
        
        <CTabContent className="p-3 border border-top-0 rounded-bottom">
          {/* Step 1: Authenticate */}
          <CTabPane visible={activeStep === 1}>
            <h5>Step 1: Authenticate with Amazon</h5>
            <p>
              This step will authenticate with Amazon and obtain the necessary access token to create and manage skills.
            </p>
            <CButton color="primary" onClick={handleAuthenticate} disabled={isLoading}>
              Authenticate with Amazon
            </CButton>
            {renderStatusIndicator()}
          </CTabPane>
          
          {/* Step 2: Create Skill */}
          <CTabPane visible={activeStep === 2}>
            <h5>Step 2: Create a New Skill</h5>
            <p>
              This step will create a new skill with the default template. 
              Your skill will be created with a predefined interaction model.
            </p>
            <CButton color="primary" onClick={handleCreateSkill} disabled={isLoading || !accessToken}>
              Create Skill
            </CButton>
            {renderStatusIndicator()}
          </CTabPane>
          
          {/* Step 3: Build Model */}
          <CTabPane visible={activeStep === 3}>
            <h5>Step 3: Build Interaction Model</h5>
            <p>
              This step will build the interaction model for your skill.
              This is required before you can test your skill.
            </p>
            <CButton color="primary" onClick={handleBuildModel} disabled={isLoading || !skillId}>
              Build Interaction Model
            </CButton>
            {renderStatusIndicator()}
          </CTabPane>
          
          {/* Step 4: Enable Testing */}
          <CTabPane visible={activeStep === 4}>
            <h5>Step 4: Enable Testing</h5>
            <p>
              This step will enable testing for your skill.
              After this, you'll be able to test your skill in the Alexa simulator.
            </p>
            <CButton color="primary" onClick={handleEnableTesting} disabled={isLoading || !skillId}>
              Enable Testing
            </CButton>
            {renderStatusIndicator()}
          </CTabPane>
          
          {/* Step 5: Submit for Certification */}
          <CTabPane visible={activeStep === 5}>
            <h5>Step 5: Submit for Certification</h5>
            <p>
              This step will submit your skill for certification.
              After certification, your skill will be available to all Alexa users.
            </p>
            <CButton color="primary" onClick={handleSubmitForCertification} disabled={isLoading || !skillId}>
              Submit for Certification
            </CButton>
            {renderStatusIndicator()}
          </CTabPane>
          
          {/* Step 6: Complete */}
          <CTabPane visible={activeStep === 6}>
            <h5>Step 6: Submission Complete</h5>
            <p>
              Your skill has been successfully submitted for certification!
            </p>
            <p>
              <strong>Skill ID:</strong> {skillId}<br />
              <strong>Status:</strong> {status}
            </p>
            <p>
              You can check the status of your submission in the Amazon Developer Console.
            </p>
            <CButton color="success" onClick={() => window.open('https://developer.amazon.com/alexa/console/ask', '_blank')}>
              Open Developer Console
            </CButton>
          </CTabPane>
        </CTabContent>
        
        <CRow className="mt-4">
          <CCol>
            {activeStep > 1 && (
              <CButton color="secondary" onClick={() => setActiveStep(activeStep - 1)}>
                Previous
              </CButton>
            )}
          </CCol>
          <CCol className="text-right">
            {activeStep < 6 && activeStep > 1 && (
              <CButton color="primary" onClick={() => setActiveStep(activeStep + 1)}>
                Skip to Next Step
              </CButton>
            )}
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  );
};

export default SkillPublisher;
